import { useMemo, useState } from 'react';
import { Select, MenuItem, FormControl } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import Routes from '@/router/Routes';
import { useLessons } from '@/api/useLessons';
import { LessonBox } from '@/components/ui/LessonBox/LessonBox';
import { useRegions } from '@/api/useRegions';
import { useSchools } from '@/api/useSchools';
import { useClasses } from '@/api/useClasses';
import { useSubjects } from '@/api/useSubjects';
import { Lesson } from '$/models/Lesson';

const PlusIcon = () => (
  <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.3334 6.83332H7.33335V11.8333H5.66669V6.83332H0.666687V5.16666H5.66669V0.166656H7.33335V5.16666H12.3334V6.83332Z" fill="white"/>
  </svg>
);

const selectStyles = (disabled?: boolean) => ({
  backgroundColor: disabled ? '#f0f0f0' : 'white',
  borderRadius: '100px',
  height: '48px',
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' as const : 'auto' as const,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiSelect-select': {
    paddingLeft: '24px',
    fontSize: '14px'
  },
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
});

const menuItemStyles = {
  fontSize: '14px'
};

const AdminDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchQuery = '' } = (useOutletContext<{ searchQuery: string }>() ?? {});
  const [regionValue, setRegionValue] = useState('');
  const [schoolValue, setSchoolValue] = useState('');
  const [classValue, setClassValue] = useState('');
  const [subjectValue, setSubjectValue] = useState('');
  const { data: lessons = [] } = useLessons(true);

  const { data: regions = [] } = useRegions();
  const { data: schools = [] } = useSchools(regionValue || undefined);
  const { data: classes = [] } = useClasses(schoolValue || undefined);
  const { data: subjects = [] } = useSubjects(classValue || undefined);

  const filteredLessons = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return lessons.filter((lesson: Lesson) => {
      if (regionValue && lesson.region_id !== regionValue) return false;
      if (schoolValue && lesson.school_id !== schoolValue) return false;
      if (classValue && lesson.class_id !== classValue) return false;
      if (subjectValue && lesson.subject_id !== subjectValue) return false;
      if (query) {
        const haystack = `${lesson.title} ${lesson.subject ?? ''} ${lesson.class ?? ''}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [lessons, regionValue, schoolValue, classValue, subjectValue, searchQuery]);

  return (
    <div className="flex flex-col gap-6">
      <div className="mb-4">
        <h1 className="text-base font-semibold text-text-secondary">{t('adminDashboard.welcome')}</h1>
        <h2 className="text-3xl font-bold">{t('adminDashboard.welcomeTitle')}</h2>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => navigate(Routes.ADMIN_ADD_REGION)}
          className="h-[48px] flex items-center justify-center gap-2 bg-[#FE7062] hover:bg-[#E5645A] text-white rounded-[120px] text-base font-semibold transition-colors"
        >
          <PlusIcon />
          {t('adminDashboard.addRegion')}
        </button>
        <button
          onClick={() => navigate(Routes.ADMIN_UPLOAD_LESSON)}
          disabled={regions.length === 0}
          className="h-[48px] flex items-center justify-center gap-2 bg-[#4B6BFB] hover:bg-[#3451DB] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-[120px] text-base font-semibold transition-colors"
        >
          <PlusIcon />
          {t('adminDashboard.newLesson')}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormControl fullWidth>
          <Select
            value={regionValue}
            onChange={(e) => { setRegionValue(e.target.value); setSchoolValue(''); setClassValue(''); setSubjectValue(''); }}
            displayEmpty
            IconComponent={KeyboardArrowDownIcon}
            sx={selectStyles()}
          >
            <MenuItem value="" sx={menuItemStyles}>
              {t('adminDashboard.filters.allRegions')}
            </MenuItem>
            {regions.map((r) => (
              <MenuItem key={r.id} value={r.id} sx={menuItemStyles}>{r.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <Select
            value={schoolValue}
            onChange={(e) => { setSchoolValue(e.target.value); setClassValue(''); setSubjectValue(''); }}
            displayEmpty
            disabled={!regionValue}
            IconComponent={KeyboardArrowDownIcon}
            sx={selectStyles(!regionValue)}
          >
            <MenuItem value="" sx={menuItemStyles}>
              {t('adminDashboard.filters.allSchools')}
            </MenuItem>
            {schools.map((s) => (
              <MenuItem key={s.id} value={s.id} sx={menuItemStyles}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <Select
            value={classValue}
            onChange={(e) => { setClassValue(e.target.value); setSubjectValue(''); }}
            displayEmpty
            disabled={!schoolValue}
            IconComponent={KeyboardArrowDownIcon}
            sx={selectStyles(!schoolValue)}
          >
            <MenuItem value="" sx={menuItemStyles}>
              {t('adminDashboard.filters.allClasses')}
            </MenuItem>
            {classes.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={menuItemStyles}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <Select
            value={subjectValue}
            onChange={(e) => setSubjectValue(e.target.value)}
            displayEmpty
            disabled={!classValue}
            IconComponent={KeyboardArrowDownIcon}
            sx={selectStyles(!classValue)}
          >
            <MenuItem value="" sx={menuItemStyles}>
              {t('adminDashboard.filters.allSubjects')}
            </MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id} sx={menuItemStyles}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="mt-6 overflow-x-auto pb-4">
        <div className="flex mt-6 gap-4 min-w-min">
          {filteredLessons.map((lesson: { id: string; title: string; subject: string | null; created_at: string; video_url: string | null }) => (
            <LessonBox
              key={lesson.id}
              id={lesson.id}
              title={lesson.title}
              subject={lesson.subject ?? ''}
              date={new Date(lesson.created_at).toLocaleDateString('pl-PL')}
              videoUrl={lesson.video_url}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
